import {
  basicPlanId,
  creatorPlanId,
} from "@jammable/ui-payments-feature/src/utils/subscriptions";
import {
  isStripeZeroDecimal,
  type Currency,
} from "@jammable/clientapi/src/payments";
import { isProduction } from "./env";

/**
 * Supported currency code. Re-exported from the canonical `Currency` type in
 * @jammable/clientapi — add new currencies there, not here. Everything below
 * (CURRENCIES, SUPPORTED_CURRENCIES, PLAN_IDS, PRICE_MAP) is keyed off this.
 */
export type CurrencyCode = Currency;

/**
 * Lowercase currency codes for API compatibility, derived from CurrencyCode.
 */
export type LowercaseCurrencyCode = Lowercase<CurrencyCode>;

/**
 * Available subscription plan types
 */
export type PlanType = "basic" | "creator" | "power";

/**
 * Plan names as they appear in the user interface and API
 */
export type PlanName =
  | "Jammable Basic"
  | "Jammable Creator"
  | "Jammable Power User";

/**
 * Available pricing tiers
 */
export type PriceTier =
  | "basic"
  | "basic-discount"
  | "creator"
  | "creator-discount"
  | "power"
  | "power-discount";

/**
 * Currency metadata including symbols and display information
 */
export interface CurrencyMetadata {
  code: CurrencyCode;
  lowercaseCode: LowercaseCurrencyCode;
  symbol: string;
  name: string;
  symbolPosition?: "before" | "after"; // Whether the symbol appears before or after the amount
}

/**
 * Comprehensive currency configuration
 * Makes it easy to add new currencies by defining all related attributes in one place
 */
export const CURRENCIES: Record<CurrencyCode, CurrencyMetadata> = {
  USD: {
    code: "USD",
    lowercaseCode: "usd",
    symbol: "$",
    name: "US Dollar",
    symbolPosition: "before",
  },
  GBP: {
    code: "GBP",
    lowercaseCode: "gbp",
    symbol: "£",
    name: "British Pound",
    symbolPosition: "before",
  },
  EUR: {
    code: "EUR",
    lowercaseCode: "eur",
    symbol: "€",
    name: "Euro",
    symbolPosition: "before",
  },
  INR: {
    code: "INR",
    lowercaseCode: "inr",
    symbol: "₹",
    name: "Indian Rupee",
    symbolPosition: "before",
  },
  IDR: {
    code: "IDR",
    lowercaseCode: "idr",
    symbol: "Rp",
    name: "Indonesian Rupiah",
    symbolPosition: "before",
  },
  MXN: {
    code: "MXN",
    lowercaseCode: "mxn",
    symbol: "MXN",
    name: "Mexican Peso",
    symbolPosition: "before",
  },
  BRL: {
    code: "BRL",
    lowercaseCode: "brl",
    symbol: "R$",
    name: "Brazilian Real",
    symbolPosition: "before",
  },
  CAD: {
    code: "CAD",
    lowercaseCode: "cad",
    symbol: "CA$",
    name: "Canadian Dollar",
    symbolPosition: "before",
  },
  AUD: {
    code: "AUD",
    lowercaseCode: "aud",
    symbol: "A$",
    name: "Australian Dollar",
    symbolPosition: "before",
  },
  PHP: {
    code: "PHP",
    lowercaseCode: "php",
    symbol: "₱",
    name: "Philippine Peso",
    symbolPosition: "before",
  },
  AED: {
    code: "AED",
    lowercaseCode: "aed",
    symbol: "AED",
    name: "UAE Dirham",
    symbolPosition: "before",
  },
  SAR: {
    code: "SAR",
    lowercaseCode: "sar",
    symbol: "SAR",
    name: "Saudi Riyal",
    symbolPosition: "before",
  },
  PLN: {
    code: "PLN",
    lowercaseCode: "pln",
    symbol: "zł",
    name: "Polish Złoty",
    symbolPosition: "before",
  },
  THB: {
    code: "THB",
    lowercaseCode: "thb",
    symbol: "฿",
    name: "Thai Baht",
    symbolPosition: "before",
  },
  MYR: {
    code: "MYR",
    lowercaseCode: "myr",
    symbol: "RM",
    name: "Malaysian Ringgit",
    symbolPosition: "before",
  },
  JPY: {
    code: "JPY",
    lowercaseCode: "jpy",
    symbol: "¥",
    name: "Japanese Yen",
    symbolPosition: "before",
  },
  KRW: {
    code: "KRW",
    lowercaseCode: "krw",
    symbol: "₩",
    name: "South Korean Won",
    symbolPosition: "before",
  },
  CHF: {
    code: "CHF",
    lowercaseCode: "chf",
    symbol: "CHF",
    name: "Swiss Franc",
    symbolPosition: "before",
  },
  ILS: {
    code: "ILS",
    lowercaseCode: "ils",
    symbol: "₪",
    name: "Israeli Shekel",
    symbolPosition: "before",
  },
  SEK: {
    code: "SEK",
    lowercaseCode: "sek",
    symbol: "kr",
    name: "Swedish Krona",
    symbolPosition: "before",
  },
  SGD: {
    code: "SGD",
    lowercaseCode: "sgd",
    symbol: "S$",
    name: "Singapore Dollar",
    symbolPosition: "before",
  },
  NZD: {
    code: "NZD",
    lowercaseCode: "nzd",
    symbol: "NZ$",
    name: "New Zealand Dollar",
    symbolPosition: "before",
  },
  CNY: {
    code: "CNY",
    lowercaseCode: "cny",
    symbol: "CN¥",
    name: "Chinese Yuan",
    symbolPosition: "before",
  },
  TRY: {
    code: "TRY",
    lowercaseCode: "try",
    symbol: "₺",
    name: "Turkish Lira",
    symbolPosition: "before",
  },
  ARS: {
    code: "ARS",
    lowercaseCode: "ars",
    symbol: "ARS$",
    name: "Argentine Peso",
    symbolPosition: "before",
  },
  PEN: {
    code: "PEN",
    lowercaseCode: "pen",
    symbol: "S/",
    name: "Peruvian Sol",
    symbolPosition: "before",
  },
  CLP: {
    code: "CLP",
    lowercaseCode: "clp",
    symbol: "CLP$",
    name: "Chilean Peso",
    symbolPosition: "before",
  },
  COP: {
    code: "COP",
    lowercaseCode: "cop",
    symbol: "COP$",
    name: "Colombian Peso",
    symbolPosition: "before",
  },
  RON: {
    code: "RON",
    lowercaseCode: "ron",
    symbol: "lei",
    name: "Romanian Leu",
    symbolPosition: "before",
  },
  VND: {
    code: "VND",
    lowercaseCode: "vnd",
    symbol: "₫",
    name: "Vietnamese Dong",
    symbolPosition: "before",
  },
  PKR: {
    code: "PKR",
    lowercaseCode: "pkr",
    symbol: "₨",
    name: "Pakistani Rupee",
    symbolPosition: "before",
  },
  UAH: {
    code: "UAH",
    lowercaseCode: "uah",
    symbol: "₴",
    name: "Ukrainian Hryvnia",
    symbolPosition: "before",
  },
  EGP: {
    code: "EGP",
    lowercaseCode: "egp",
    symbol: "E£",
    name: "Egyptian Pound",
    symbolPosition: "before",
  },
  BOB: {
    code: "BOB",
    lowercaseCode: "bob",
    symbol: "Bs",
    name: "Bolivian Boliviano",
    symbolPosition: "before",
  },
  BDT: {
    code: "BDT",
    lowercaseCode: "bdt",
    symbol: "৳",
    name: "Bangladeshi Taka",
    symbolPosition: "before",
  },
};

/**
 * Get an array of all supported currency codes
 * Useful for validation and UI dropdowns
 */
export const SUPPORTED_CURRENCIES = Object.keys(CURRENCIES) as CurrencyCode[];

/**
 * Lowercase supported currency codes. Single source for any place that needs to
 * validate an incoming/geo currency (e.g. the subscription API allow-list).
 */
export const SUPPORTED_CURRENCIES_LOWERCASE: LowercaseCurrencyCode[] =
  SUPPORTED_CURRENCIES.map((c) => CURRENCIES[c].lowercaseCode);

/**
 * Map of plan types to plan names
 * Used for displaying plan names in the UI and for API compatibility
 */
export const PLAN_NAMES: Record<PlanType, PlanName> = {
  basic: "Jammable Basic",
  creator: "Jammable Creator",
  power: "Jammable Power User",
};

/**
 * Structured mapping for plan IDs
 * Instead of accessing plan IDs by array index, we use a meaningful structure
 * that clearly shows which ID is for which plan and currency
 */
// Explicit Stripe price ID per plan + currency. Kept as literals (not array
// indices) so reordering the *PlanId arrays can't silently remap a currency.
// Amounts in comments are the live Stripe recurring price at time of writing.
//
// !! Adding a price here is NOT enough. Every ID must also be added to
// basicPlanId / creatorPlanId / powerPlanId in
// packages/ui/features/payments/src/utils/subscriptions.ts. Those arrays are
// what getSubscriptionStatus (entitlement) and getDiscountCode (first-month
// coupon) match against. Missing them means the customer pays, receives no
// access, and is charged the full price instead of the intro price - see the
// 2026-08 currency expansion, where 12 currencies shipped without them.
export const PLAN_IDS: Record<PlanType, Record<CurrencyCode, string>> = {
  basic: {
    USD: "price_1NVMJTCUwJnq3QQBbxqgtALq", // $8.99/mo
    GBP: "price_1NVMKmCUwJnq3QQBgJk67lD1", // £8.99/mo
    EUR: "price_1NVMKQCUwJnq3QQBWLra0VwZ", // €8.99/mo
    INR: "price_1NxYHHCUwJnq3QQBe62v7xcL", // ₹399/mo
    IDR: "price_1QwMT0CUwJnq3QQBSmScFedd", // Rp35000/mo
    MXN: "price_1RBFLmCUwJnq3QQB894WigS4", // MX$79/mo
    BRL: "price_1RBFvaCUwJnq3QQB6g6qjEQu", // R$12.99/mo
    CAD: "price_1Tb6iVCUwJnq3QQBO7YevEb7", // CA$11.99/mo
    AUD: "price_1Tb6inCUwJnq3QQBMnC5TiWb", // A$12.99/mo
    PHP: "price_1Tb6itCUwJnq3QQBHQhbrFH3", // ₱249/mo
    AED: "price_1Tb77gCUwJnq3QQBwxmH4rEM", // AED 32.99/mo
    SAR: "price_1Tb77iCUwJnq3QQBHhkQfV64", // SAR 32.99/mo
    PLN: "price_1Tb77jCUwJnq3QQBTpFXqBVw", // zł34.99/mo
    THB: "price_1Tb77lCUwJnq3QQBy2mViC3Z", // ฿149/mo
    MYR: "price_1Tb77nCUwJnq3QQBaE7UaX2c", // RM19.99/mo
    JPY: "price_1Tb77oCUwJnq3QQBYmeI3OfT", // ¥1300/mo
    KRW: "price_1Tb77qCUwJnq3QQBlQoWZ6Ex", // ₩11900/mo
    CHF: "price_1TbFxVCUwJnq3QQBuaCVmj5h", // CHF 8.99/mo
    ILS: "price_1TbFxzCUwJnq3QQBxsxQD6zC", // ₪32.99/mo
    SEK: "price_1TbFy0CUwJnq3QQByIF0Tusf", // kr99/mo
    SGD: "price_1TbFy2CUwJnq3QQBfo8qV3yB", // S$11.99/mo
    NZD: "price_1TbFy4CUwJnq3QQBsL0KHfpt", // NZ$14.99/mo
    CNY: "price_1TbFy6CUwJnq3QQB9QDgcflv", // CN¥68/mo
    TRY: "price_1U082qCUwJnq3QQBT6iYrkiH", // 249/mo
    ARS: "price_1U082sCUwJnq3QQBhgY2aOET", // 6999/mo
    PEN: "price_1U082tCUwJnq3QQBup3kbHUe", // 14.99/mo
    CLP: "price_1U082uCUwJnq3QQBeGOEJMqi", // 4990/mo
    COP: "price_1U082wCUwJnq3QQBaSzMctNr", // 12900/mo
    RON: "price_1U082xCUwJnq3QQBISCegvYw", // 24.99/mo
    VND: "price_1U082yCUwJnq3QQBzzH7s4rI", // 79000/mo
    PKR: "price_1U092HCUwJnq3QQBheYK1FjS", // 749/mo
    UAH: "price_1U092ICUwJnq3QQBBJ7vfFfk", // 139/mo
    EGP: "price_1U092JCUwJnq3QQBnQrDXyaI", // 139/mo
    BOB: "price_1U092LCUwJnq3QQB4HuBEYHR", // 40.99/mo
    BDT: "price_1U092MCUwJnq3QQBQh360wZc", // 329/mo
  },
  creator: {
    USD: "price_1MwYlgCUwJnq3QQBaxn8t4Jf", // $24.99/mo
    GBP: "price_1N7K8xCUwJnq3QQBHhm5Y4IU", // £24.99/mo
    EUR: "price_1N7K9xCUwJnq3QQBBiH8Uk5j", // €24.99/mo
    INR: "price_1NxYHlCUwJnq3QQB0TTeBpXb", // ₹1249/mo
    IDR: "price_1QwN5PCUwJnq3QQBTiQSOFdz", // Rp100000/mo
    MXN: "price_1RBEMkCUwJnq3QQBUC8h52zg", // MX$249/mo
    BRL: "price_1RBFv1CUwJnq3QQBO8UDfNzf", // R$49.99/mo
    CAD: "price_1Tb6ikCUwJnq3QQB1az0xQBF", // CA$33.99/mo
    AUD: "price_1Tb6ipCUwJnq3QQBb16lssCE", // A$36.99/mo
    PHP: "price_1Tb6iuCUwJnq3QQB6DtiKSw4", // ₱699/mo
    AED: "price_1Tb77yCUwJnq3QQBY6BezgyJ", // AED 89.99/mo
    SAR: "price_1Tb780CUwJnq3QQBOiTICfwr", // SAR 89.99/mo
    PLN: "price_1Tb781CUwJnq3QQBLtNQps10", // zł99.99/mo
    THB: "price_1Tb783CUwJnq3QQBi6Bm4QgB", // ฿399/mo
    MYR: "price_1Tb784CUwJnq3QQBx86Vvd5M", // RM49.99/mo
    JPY: "price_1Tb786CUwJnq3QQBjKVDzcqg", // ¥3700/mo
    KRW: "price_1Tb787CUwJnq3QQBOW96T3Cu", // ₩33000/mo
    CHF: "price_1TbFyFCUwJnq3QQBewVwtWrB", // CHF 24.99/mo
    ILS: "price_1TbFyHCUwJnq3QQBY8gk6z4D", // ₪92.99/mo
    SEK: "price_1TbFyICUwJnq3QQBX8TFeS3Q", // kr269/mo
    SGD: "price_1TbFyKCUwJnq3QQBiPzPjtAo", // S$33.99/mo
    NZD: "price_1TbFyMCUwJnq3QQBQEvfP4Qw", // NZ$41.99/mo
    CNY: "price_1TbFyOCUwJnq3QQBGHI0e1CO", // CN¥178/mo
    TRY: "price_1U082rCUwJnq3QQBRGwDZ6Qt", // 649/mo
    ARS: "price_1U082sCUwJnq3QQBX9QPfjK0", // 18999/mo
    PEN: "price_1U082uCUwJnq3QQBE40qwhiV", // 42.99/mo
    CLP: "price_1U082vCUwJnq3QQBXhc7fnjh", // 13990/mo
    COP: "price_1U082wCUwJnq3QQBmDek93Rn", // 35900/mo
    RON: "price_1U082xCUwJnq3QQBSf97ocsO", // 69.99/mo
    VND: "price_1U082zCUwJnq3QQBzAZBLnQv", // 229000/mo
    PKR: "price_1U092HCUwJnq3QQBoibrvkLN", // 2100/mo
    UAH: "price_1U092JCUwJnq3QQBkDOjoEvJ", // 389/mo
    EGP: "price_1U092KCUwJnq3QQB9cIsHP27", // 379/mo
    BOB: "price_1U092LCUwJnq3QQBYNPSZy37", // 119/mo
    BDT: "price_1U092MCUwJnq3QQBJdASZbPk", // 929/mo
  },
  power: {
    USD: "price_1MwYn7CUwJnq3QQB6mg34Jf9", // $89.99/mo
    GBP: "price_1N7KEkCUwJnq3QQB3EmjwmBP", // £89.99/mo
    EUR: "price_1N7KFDCUwJnq3QQBzn08BY8B", // €89.99/mo
    INR: "price_1NxYI3CUwJnq3QQB8MXWWU1a", // ₹4499/mo
    IDR: "",
    MXN: "",
    BRL: "",
    CAD: "price_1Tb6ilCUwJnq3QQBqD90yA2D", // CA$119.99/mo
    AUD: "price_1Tb6itCUwJnq3QQB5j3FyuoU", // A$134.99/mo
    PHP: "price_1Tb6ivCUwJnq3QQB3iqjprYB", // ₱2499/mo
    AED: "price_1Tb78ECUwJnq3QQBpXr7lDd5", // AED 329.99/mo
    SAR: "price_1Tb78GCUwJnq3QQB9JPFJzUM", // SAR 329.99/mo
    PLN: "price_1Tb78ICUwJnq3QQBmpYxxYJc", // zł359.99/mo
    THB: "price_1Tb78JCUwJnq3QQBeirbAIjh", // ฿1599/mo
    MYR: "price_1Tb78LCUwJnq3QQBVBYwfTas", // RM199/mo
    JPY: "price_1Tb78MCUwJnq3QQBXYxdCU6m", // ¥13500/mo
    KRW: "price_1Tb78OCUwJnq3QQB2jkhPyuS", // ₩119000/mo
    CHF: "price_1TbFyWCUwJnq3QQBzcoH8S98", // CHF 89.99/mo
    ILS: "price_1TbFyXCUwJnq3QQBqffUuvcr", // ₪334.99/mo
    SEK: "price_1TbFyZCUwJnq3QQB515qmHC6", // kr949/mo
    SGD: "price_1TbFybCUwJnq3QQBAlp4mgoR", // S$119.99/mo
    NZD: "price_1TbFydCUwJnq3QQBywFye6UN", // NZ$149.99/mo
    CNY: "price_1TbFyeCUwJnq3QQBreeO5rPn", // CN¥638/mo
    TRY: "price_1U082rCUwJnq3QQBW3ycL0r9", // 2349/mo
    ARS: "price_1U082tCUwJnq3QQBqcEvPwhQ", // 66999/mo
    PEN: "price_1U082uCUwJnq3QQBfE6RHpGc", // 149.99/mo
    CLP: "price_1U082vCUwJnq3QQByUjc6kYm", // 49990/mo
    COP: "price_1U082xCUwJnq3QQBzhc6nwqz", // 129900/mo
    RON: "price_1U082yCUwJnq3QQBdK5oFaFP", // 249.99/mo
    VND: "price_1U082zCUwJnq3QQBeP0oanER", // 819000/mo
    PKR: "price_1U092ICUwJnq3QQBXe7bk4vq", // 7500/mo
    UAH: "price_1U092JCUwJnq3QQBPz0DIWZr", // 1400/mo
    EGP: "price_1U092KCUwJnq3QQBkPRKAjYn", // 1400/mo
    BOB: "price_1U092MCUwJnq3QQBK2StVUXx", // 409/mo
    BDT: "price_1U092NCUwJnq3QQBjKfXeu6l", // 3300/mo
  },
};

/**
 * Discount coupon codes for different plan types and currencies
 * Structured by environment (production/development), plan type, and currency
 */
export const DISCOUNT_CODES = {
  production: {
    basic: {
      USD: "gyBcazlG", // 1.99
      GBP: "EwzMJuDg", // 1.99
      EUR: "KFYEmATR", // 1.99
      INR: "Q4VGFfHD", // 1.99
      IDR: "swKEquof", //
      MXN: "rRCeO3zl", // 1.99
      BRL: "T0qnHGj9", // 1.99
      CAD: "pEVmg1EN", // CA$11.99 -> 2.99 first month
      AUD: "btXjC39k", // A$12.99 -> 2.99 first month
      PHP: "t82m9LMY", // ₱249 -> 49 first month
      AED: "M58EUmay", // AED 32.99 -> 6.99 first month
      SAR: "ZFCCtx2A", // SAR 32.99 -> 6.99 first month
      PLN: "6DbzuEj5", // zł34.99 -> 7.99 first month
      THB: "8r5gSHUl", // ฿149 -> 29 first month
      MYR: "urZdFmkO", // RM19.99 -> 4.99 first month
      JPY: "G1OQdpOZ", // ¥1300 -> 300 first month
      KRW: "Jw1CGnrg", // ₩11900 -> 2900 first month
      CHF: "Q3MKBSj4", // CHF 8.99 -> 1.99 first month
      ILS: "j8hzMH6D", // ₪32.99 -> 7.99 first month
      SEK: "eFZ3KhKc", // kr99 -> 22 first month
      SGD: "6ybpYQC3", // S$11.99 -> 2.99 first month
      NZD: "o47zgm3w", // NZ$14.99 -> 3.49 first month
      CNY: "P13Ku7xm", // CN¥68 -> 15 first month
      TRY: "IiCbAb2v", // 249 -> 49 first month
      ARS: "FUK2ZcMH", // 6999 -> 1499 first month
      PEN: "al818MTM", // 14.99 -> 3.49 first month
      CLP: "TgudyAvj", // 4990 -> 1090 first month
      COP: "Iw9dQv3V", // 12900 -> 2900 first month
      RON: "eg0260qS", // 24.99 -> 5.49 first month
      VND: "2uclMiw3", // 79000 -> 19000 first month
      PKR: "959yqEtD", // 749 -> 169 first month
      UAH: "Ss8ydl5A", // 139 -> 30.99 first month
      EGP: "o1sB2qP4", // 139 -> 30.99 first month
      BOB: "yUDxG2kY", // 40.99 -> 8.99 first month
      BDT: "I7YCahZe", // 329 -> 73.99 first month
    },
    creator: {
      USD: "zRsoR2yg",
      GBP: "JGNYODik",
      EUR: "J3htmHI8",
      INR: "LG7KIwnf", // No specific code for INR yet
      IDR: "DusOxDcm", // No specific code for IDR yet
      MXN: "jd8iSRMb", // 1.99
      BRL: "Z9zl30qj", // 1.99
      CAD: "ynfJm6uz", // CA$33.99 -> 13.99 first month
      AUD: "tssT3fZM", // A$36.99 -> 14.99 first month
      PHP: "ABJcnUmW", // ₱699 -> 299 first month
      AED: "WTlSrS5o", // AED 89.99 -> 36.99 first month
      SAR: "JvOCr5lv", // SAR 89.99 -> 36.99 first month
      PLN: "IXJbCkLk", // zł99.99 -> 39.99 first month
      THB: "73HYiHgy", // ฿399 -> 169 first month
      MYR: "fRlw60nB", // RM49.99 -> 21.99 first month
      JPY: "Wc9vG0sc", // ¥3700 -> 1500 first month
      KRW: "yCfmTrVN", // ₩33000 -> 13000 first month
      CHF: "70JfqHb0", // CHF 24.99 -> 9.99 first month
      ILS: "rthDPHxa", // ₪92.99 -> 36.99 first month
      SEK: "eXIgWy4G", // kr269 -> 109 first month
      SGD: "f65TYORX", // S$33.99 -> 13.99 first month
      NZD: "jSpTF0Wt", // NZ$41.99 -> 16.99 first month
      CNY: "kkoqmYAx", // CN¥178 -> 72 first month
      TRY: "XE1XZJ5P", // 649 -> 259 first month
      ARS: "c7HhfZJf", // 18999 -> 7499 first month
      PEN: "HONoY4CG", // 42.99 -> 16.99 first month
      CLP: "MnLgaORF", // 13990 -> 5490 first month
      COP: "zo9lr5NJ", // 35900 -> 14900 first month
      RON: "L5UD7hRN", // 69.99 -> 26.99 first month
      VND: "ML1Xi87O", // 229000 -> 89000 first month
      PKR: "4e3tEMqL", // 2100 -> 829 first month
      UAH: "xr5zzeUp", // 389 -> 159 first month
      EGP: "ZUwVCvay", // 379 -> 149 first month
      BOB: "3t8NeLZB", // 119 -> 45.99 first month
      BDT: "hr1VBPY5", // 929 -> 369 first month
    },
  },
};

/**
 * Price mapping for each tier and currency
 * This makes it easy to add new currencies or adjust prices
 */
export const PRICE_MAP: Record<PriceTier, Record<CurrencyCode, number>> = {
  basic: {
    USD: 1.99,
    GBP: 1.99,
    EUR: 1.99,
    INR: 99,
    IDR: 8999,
    MXN: 19,
    BRL: 2.99,
    CAD: 2.99,
    AUD: 2.99,
    PHP: 49,
    AED: 6.99,
    SAR: 6.99,
    PLN: 7.99,
    THB: 29,
    MYR: 4.99,
    JPY: 300,
    KRW: 2900,
    CHF: 1.99,
    ILS: 7.99,
    SEK: 22,
    SGD: 2.99,
    NZD: 3.49,
    CNY: 15,
    TRY: 49,
    ARS: 1499,
    PEN: 3.49,
    CLP: 1090,
    COP: 2900,
    RON: 5.49,
    VND: 19000,
    PKR: 169,
    UAH: 30.99,
    EGP: 30.99,
    BOB: 8.99,
    BDT: 73.99,
  },
  "basic-discount": {
    USD: 8.99,
    GBP: 8.99,
    EUR: 8.99,
    INR: 399,
    IDR: 35000,
    MXN: 79,
    BRL: 12.99,
    CAD: 11.99,
    AUD: 12.99,
    PHP: 249,
    AED: 32.99,
    SAR: 32.99,
    PLN: 34.99,
    THB: 149,
    MYR: 19.99,
    JPY: 1300,
    KRW: 11900,
    CHF: 8.99,
    ILS: 32.99,
    SEK: 99,
    SGD: 11.99,
    NZD: 14.99,
    CNY: 68,
    TRY: 249,
    ARS: 6999,
    PEN: 14.99,
    CLP: 4990,
    COP: 12900,
    RON: 24.99,
    VND: 79000,
    PKR: 749,
    UAH: 139,
    EGP: 139,
    BOB: 40.99,
    BDT: 329,
  },
  creator: {
    USD: 9.99,
    GBP: 9.99,
    EUR: 9.99,
    INR: 349,
    IDR: 29999,
    MXN: 99,
    BRL: 14.99,
    CAD: 13.99,
    AUD: 14.99,
    PHP: 299,
    AED: 36.99,
    SAR: 36.99,
    PLN: 39.99,
    THB: 169,
    MYR: 21.99,
    JPY: 1500,
    KRW: 13000,
    CHF: 9.99,
    ILS: 36.99,
    SEK: 109,
    SGD: 13.99,
    NZD: 16.99,
    CNY: 72,
    TRY: 259,
    ARS: 7499,
    PEN: 16.99,
    CLP: 5490,
    COP: 14900,
    RON: 26.99,
    VND: 89000,
    PKR: 829,
    UAH: 159,
    EGP: 149,
    BOB: 45.99,
    BDT: 369,
  },
  "creator-discount": {
    USD: 24.99,
    GBP: 24.99,
    EUR: 24.99,
    INR: 1249,
    IDR: 100000,
    MXN: 249,
    BRL: 49.99,
    CAD: 33.99,
    AUD: 36.99,
    PHP: 699,
    AED: 89.99,
    SAR: 89.99,
    PLN: 99.99,
    THB: 399,
    MYR: 49.99,
    JPY: 3700,
    KRW: 33000,
    CHF: 24.99,
    ILS: 92.99,
    SEK: 269,
    SGD: 33.99,
    NZD: 41.99,
    CNY: 178,
    TRY: 649,
    ARS: 18999,
    PEN: 42.99,
    CLP: 13990,
    COP: 35900,
    RON: 69.99,
    VND: 229000,
    PKR: 2100,
    UAH: 389,
    EGP: 379,
    BOB: 119,
    BDT: 929,
  },
  power: {
    USD: 89.99,
    GBP: 89.99,
    EUR: 89.99,
    INR: 4499,
    IDR: 1349999,
    MXN: 899,
    BRL: 149.99,
    CAD: 119.99,
    AUD: 134.99,
    PHP: 2499,
    AED: 329.99,
    SAR: 329.99,
    PLN: 359.99,
    THB: 1599,
    MYR: 199,
    JPY: 13500,
    KRW: 119000,
    CHF: 89.99,
    ILS: 334.99,
    SEK: 949,
    SGD: 119.99,
    NZD: 149.99,
    CNY: 638,
    TRY: 2349,
    ARS: 66999,
    PEN: 149.99,
    CLP: 49990,
    COP: 129900,
    RON: 249.99,
    VND: 819000,
    PKR: 7500,
    UAH: 1400,
    EGP: 1400,
    BOB: 409,
    BDT: 3300,
  },
  "power-discount": {
    USD: 89.99,
    GBP: 89.99,
    EUR: 89.99,
    INR: 4499,
    IDR: 1349999,
    MXN: 899,
    BRL: 149.99,
    CAD: 119.99,
    AUD: 134.99,
    PHP: 2499,
    AED: 329.99,
    SAR: 329.99,
    PLN: 359.99,
    THB: 1599,
    MYR: 199,
    JPY: 13500,
    KRW: 119000,
    CHF: 89.99,
    ILS: 334.99,
    SEK: 949,
    SGD: 119.99,
    NZD: 149.99,
    CNY: 638,
    TRY: 2349,
    ARS: 66999,
    PEN: 149.99,
    CLP: 49990,
    COP: 129900,
    RON: 249.99,
    VND: 819000,
    PKR: 7500,
    UAH: 1400,
    EGP: 1400,
    BOB: 409,
    BDT: 3300,
  },
};

/**
 * Annual pricing. Rolling out per-currency — only currencies present in
 * ANNUAL_PLAN_IDS offer an annual option, so gate the UI toggle on
 * `isAnnualAvailable`. Annual has no first-month coupon; the yearly saving is
 * the incentive.
 */
export const ANNUAL_PLAN_IDS: Partial<
  Record<CurrencyCode, Partial<Record<PlanType, string>>>
> = {
  GBP: {
    basic: "price_1TbIsDCUwJnq3QQBYOHsZwEK", // £44.99/yr
    creator: "price_1TbIsJCUwJnq3QQBHjig9KZ9", // £119.99/yr
    power: "price_1TbIsKCUwJnq3QQBVgcmc5PF", // £599.99/yr
  },
  USD: {
    basic: "price_1TbRalCUwJnq3QQB2WR5Ja0m", // $44.99/yr
    creator: "price_1TbRasCUwJnq3QQBsrDZayqV", // $119.99/yr
    power: "price_1TbRatCUwJnq3QQBzGD6vuzG", // $599.99/yr
  },
  EUR: {
    basic: "price_1TbSK0CUwJnq3QQBExF6VJvH", // €44.99/yr
    creator: "price_1TbSKACUwJnq3QQBO4kvgBIo", // €119.99/yr
    power: "price_1TbSKCCUwJnq3QQBYwfl3GUd", // €599.99/yr
  },
  CAD: {
    basic: "price_1TbSKDCUwJnq3QQBY6IgjjH2", // CA$59.99/yr
    creator: "price_1TbSKFCUwJnq3QQBEXLsAwF2", // CA$159.99/yr
    power: "price_1TbSKHCUwJnq3QQBeIAhdMVZ", // CA$799.99/yr
  },
  AUD: {
    basic: "price_1TbSKJCUwJnq3QQB6sBc49Wz", // A$64.99/yr
    creator: "price_1TbSKKCUwJnq3QQBHL17jXDV", // A$179.99/yr
    power: "price_1TbSKMCUwJnq3QQBSm2RTKHk", // A$899.99/yr
  },
  INR: { basic: "price_1UNcY1CUwJnq3QQBCSDqnWrx" }, // ₹1,999/yr (2026-10, Basic only)
  IDR: { basic: "price_1UNcY1CUwJnq3QQBhR1GdkN4" }, // Rp175,000/yr (2026-10, Basic only)
  MXN: { basic: "price_1UNcY2CUwJnq3QQBps77g8aQ" }, // MX$399/yr (2026-10, Basic only)
  BRL: { basic: "price_1UNcY2CUwJnq3QQBWep867Cm" }, // R$64.99/yr (2026-10, Basic only)
  PHP: { basic: "price_1UNcY2CUwJnq3QQBFLGyin3J" }, // ₱1,249/yr (2026-10, Basic only)
  AED: { basic: "price_1UNcY2CUwJnq3QQBeRXcgReL" }, // AED 164.99/yr (2026-10, Basic only)
  SAR: { basic: "price_1UNcY3CUwJnq3QQB7SbvKcfZ" }, // SAR 164.99/yr (2026-10, Basic only)
  PLN: { basic: "price_1UNcY3CUwJnq3QQBvyigvDIk" }, // zł174.99/yr (2026-10, Basic only)
  THB: { basic: "price_1UNcY3CUwJnq3QQBylwj50tv" }, // ฿749/yr (2026-10, Basic only)
  MYR: { basic: "price_1UNcY4CUwJnq3QQBBhdTSJUD" }, // RM99.99/yr (2026-10, Basic only)
  JPY: { basic: "price_1UNcY4CUwJnq3QQBkkPhsm0P" }, // ¥6,500/yr (2026-10, Basic only)
  KRW: { basic: "price_1UNcY4CUwJnq3QQBDMpAz0Vt" }, // ₩59,900/yr (2026-10, Basic only)
  CHF: { basic: "price_1UNcY4CUwJnq3QQBSXXFyvRX" }, // CHF 44.99/yr (2026-10, Basic only)
  ILS: { basic: "price_1UNcY5CUwJnq3QQBG0qpXWPd" }, // ₪164.99/yr (2026-10, Basic only)
  SEK: { basic: "price_1UNcY5CUwJnq3QQBtCQETNo7" }, // kr499/yr (2026-10, Basic only)
  SGD: { basic: "price_1UNcY5CUwJnq3QQBJvpWPTlN" }, // S$59.99/yr (2026-10, Basic only)
  NZD: { basic: "price_1UNcY5CUwJnq3QQBF7PxTRrj" }, // NZ$74.99/yr (2026-10, Basic only)
  CNY: { basic: "price_1UNcY6CUwJnq3QQBQiCpO9TU" }, // CN¥339/yr (2026-10, Basic only)
  TRY: { basic: "price_1UNcY6CUwJnq3QQBk9kRxOPj" }, // ₺1,249/yr (2026-10, Basic only)
  ARS: { basic: "price_1UNcY6CUwJnq3QQBvq4wMxf3" }, // ARS$34,999/yr (2026-10, Basic only)
  PEN: { basic: "price_1UNcY6CUwJnq3QQBaNPHuuvc" }, // S/74.99/yr (2026-10, Basic only)
  CLP: { basic: "price_1UNcY7CUwJnq3QQBGdEd9DFP" }, // CLP$24,990/yr (2026-10, Basic only)
  COP: { basic: "price_1UNcY7CUwJnq3QQB7Pjf0E3L" }, // COL$64,900/yr (2026-10, Basic only)
  RON: { basic: "price_1UNcY7CUwJnq3QQB4l8WLAHg" }, // lei 124.99/yr (2026-10, Basic only)
  VND: { basic: "price_1UNcY7CUwJnq3QQBdQ5SRyMb" }, // ₫395,000/yr (2026-10, Basic only)
  PKR: { basic: "price_1UNcY8CUwJnq3QQBCokuVBlQ" }, // Rs3,749/yr (2026-10, Basic only)
  UAH: { basic: "price_1UNcY8CUwJnq3QQBhFUG4mur" }, // ₴699/yr (2026-10, Basic only)
  EGP: { basic: "price_1UNcY8CUwJnq3QQBdeDuclyn" }, // E£699/yr (2026-10, Basic only)
  BOB: { basic: "price_1UNcY9CUwJnq3QQBEqw87FUq" }, // Bs204.99/yr (2026-10, Basic only)
  BDT: { basic: "price_1UNcY9CUwJnq3QQBbW7ufjBv" }, // ৳1,649/yr (2026-10, Basic only)
};

/** Annual display price (major unit) per plan, for currencies that offer it. */
export const ANNUAL_PRICE_MAP: Partial<
  Record<CurrencyCode, Partial<Record<PlanType, number>>>
> = {
  GBP: { basic: 44.99, creator: 119.99, power: 599.99 },
  USD: { basic: 44.99, creator: 119.99, power: 599.99 },
  EUR: { basic: 44.99, creator: 119.99, power: 599.99 },
  CAD: { basic: 59.99, creator: 159.99, power: 799.99 },
  AUD: { basic: 64.99, creator: 179.99, power: 899.99 },
  INR: { basic: 1999 },
  IDR: { basic: 175000 },
  MXN: { basic: 399 },
  BRL: { basic: 64.99 },
  PHP: { basic: 1249 },
  AED: { basic: 164.99 },
  SAR: { basic: 164.99 },
  PLN: { basic: 174.99 },
  THB: { basic: 749 },
  MYR: { basic: 99.99 },
  JPY: { basic: 6500 },
  KRW: { basic: 59900 },
  CHF: { basic: 44.99 },
  ILS: { basic: 164.99 },
  SEK: { basic: 499 },
  SGD: { basic: 59.99 },
  NZD: { basic: 74.99 },
  CNY: { basic: 339 },
  TRY: { basic: 1249 },
  ARS: { basic: 34999 },
  PEN: { basic: 74.99 },
  CLP: { basic: 24990 },
  COP: { basic: 64900 },
  RON: { basic: 124.99 },
  VND: { basic: 395000 },
  PKR: { basic: 3749 },
  UAH: { basic: 699 },
  EGP: { basic: 699 },
  BOB: { basic: 204.99 },
  BDT: { basic: 1649 },
};

/** Whether annual billing is offered in this currency. */
export function isAnnualAvailable(currency: string): boolean {
  return (currency.toUpperCase() as CurrencyCode) in ANNUAL_PLAN_IDS;
}

/** Annual Stripe price ID for a plan+currency, or null if not offered. */
export function getAnnualPriceId(
  plan: PlanType,
  currency: string,
): string | null {
  return ANNUAL_PLAN_IDS[currency.toUpperCase() as CurrencyCode]?.[plan] ?? null;
}

/** Annual display price for a plan+currency, or null if not offered. */
export function getAnnualPrice(plan: PlanType, currency: string): number | null {
  return ANNUAL_PRICE_MAP[currency.toUpperCase() as CurrencyCode]?.[plan] ?? null;
}

const ANNUAL_PRICE_ID_SET = new Set<string>(
  Object.values(ANNUAL_PLAN_IDS).flatMap((byPlan) =>
    Object.values(byPlan ?? {}),
  ),
);

/** True if the price ID is an annual price. */
export function isAnnualPriceId(priceId: string): boolean {
  return ANNUAL_PRICE_ID_SET.has(priceId);
}

/**
 * Gets the appropriate price ID for a specific plan and currency.
 * For interval "year", returns the annual price when the currency offers it.
 * Falls back to USD (monthly) if the specified currency is not supported.
 *
 * @param plan - The subscription plan type
 * @param currency - The currency code
 * @param interval - "month" (default) or "year"
 * @returns The corresponding price ID
 */
export function getPriceId(
  plan: PlanType,
  currency: string,
  interval: "month" | "year" = "month",
): string {
  if (interval === "year") {
    const annual = getAnnualPriceId(plan, currency);
    if (annual) return annual;
  }
  const currencyCode = currency as CurrencyCode;
  // Check if the currency exists in our mapping, otherwise default to USD
  return PLAN_IDS[plan][currencyCode] || PLAN_IDS[plan]["USD"];
}

/**
 * The currency a plan is actually charged in: the visitor's, when Stripe has
 * a price for that plan there, else USD (getPriceId falls back the same way,
 * and PricingCard sends that USD price). Show a plan's price in this
 * currency: PRICE_MAP has local Power prices for IDR, MXN and BRL that no
 * Stripe price backs, so showing those charged dollars at a local figure.
 */
export function chargeCurrency(
  plan: PlanType,
  currency: string,
  interval: "month" | "year" = "month",
): CurrencyCode {
  const c = currency.toUpperCase() as CurrencyCode;
  if (interval === "year" && getAnnualPriceId(plan, c)) return c;
  return PLAN_IDS[plan]?.[c] ? c : "USD";
}

/**
 * Gets the appropriate price ID by plan name and currency (lowercase)
 * For compatibility with the subscription API that uses plan names and lowercase currencies
 *
 * @param planName - The plan name (e.g., "Jammable Creator")
 * @param currency - The lowercase currency code (e.g., "usd")
 * @returns The corresponding price ID
 */
export function getPriceIdByNameAndCurrency(
  planName: PlanName,
  currency: LowercaseCurrencyCode,
): string {
  const plan = planName
    .replace("Jammable ", "")
    .trim()
    .toLowerCase() as PlanType;
  return PLAN_IDS[plan][currency.toUpperCase() as CurrencyCode];
}

/**
 * Gets the appropriate price amount for a tier and currency
 * Falls back to USD if the specified currency is not supported
 *
 * @param currency - The currency code
 * @param tier - The price tier
 * @returns The price amount in the specified currency
 */
export function getPrice(currency: string, tier: string): number | null {
  // Check if the tier is valid
  if (!Object.keys(PRICE_MAP).includes(tier)) {
    return null;
  }

  const priceTier = tier as PriceTier;
  const currencyCode = currency as CurrencyCode;

  // If the currency exists in our mapping, return the price, otherwise default to USD
  return PRICE_MAP[priceTier][currencyCode] || PRICE_MAP[priceTier]["USD"];
}

/**
 * Gets the symbol for a given currency code
 * Falls back to $ if the currency is not supported
 *
 * @param currency - The currency code
 * @returns The currency symbol
 */
export function getCurrencySymbol(currency: string): string {
  const currencyCode = currency as CurrencyCode;
  return CURRENCIES[currencyCode]?.symbol || CURRENCIES["USD"].symbol;
}

/**
 * Format a price with the appropriate currency symbol
 *
 * @param amount - The price amount
 * @param currency - The currency code
 * @returns Formatted price with symbol
 */
export function formatPrice(amount: number, currency: string): string {
  const currencyCode = currency as CurrencyCode;
  const currencyInfo = CURRENCIES[currencyCode] || CURRENCIES["USD"];

  return currencyInfo.symbolPosition === "after"
    ? `${amount}${currencyInfo.symbol}`
    : `${currencyInfo.symbol}${amount}`;
}

/**
 * Gets the appropriate discount code for a price ID and currency
 *
 * @param priceId - The price ID to check
 * @param currency - The currency code
 * @returns The corresponding discount code or null if not found
 */
export function getDiscountCode(
  priceId: string,
  currency: string,
): string | null {
  const currencyCode = currency as CurrencyCode;

  // Annual prices already bake in their discount — never auto-apply the
  // monthly first-month coupon to an annual subscription.
  if (isAnnualPriceId(priceId)) return null;

  // Determine plan type based on price ID
  let planType: PlanType | null = null;

  if (basicPlanId.includes(priceId)) {
    planType = "basic";
  } else if (creatorPlanId.includes(priceId)) {
    planType = "creator";
  }

  // Return null if not a recognized plan type
  if (!planType) return null;

  // Get the discount code, falling back to USD if the currency is not supported
  return (
    DISCOUNT_CODES["production"][planType][currencyCode] ||
    DISCOUNT_CODES["production"][planType]["USD"]
  );
}

/**
 * Gets the discount coupon code for a plan type and currency
 *
 * @param planType - The plan type ("basic" or "creator")
 * @param currency - The currency code (lowercase, e.g., "usd")
 * @returns The coupon code or null if not found
 */
export function getDiscountCodeByPlan(
  planType: string,
  currency: string,
): string | null {
  const normalizedPlan = planType.toLowerCase() as PlanType;
  const currencyCode = currency.toUpperCase() as CurrencyCode;

  const planCodes =
    DISCOUNT_CODES["production"][
      normalizedPlan as keyof (typeof DISCOUNT_CODES)["production"]
    ];
  if (!planCodes) return null;

  return planCodes[currencyCode] || planCodes["USD"] || null;
}

/**
 * Converts uppercase currency code to lowercase for API compatibility
 *
 * @param currency - The uppercase currency code (e.g., "USD")
 * @returns The lowercase currency code (e.g., "usd")
 */
export function currencyToLowercase(currency: string): LowercaseCurrencyCode {
  return currency.toLowerCase() as LowercaseCurrencyCode;
}

/**
 * Converts lowercase currency code to uppercase for internal use
 *
 * @param currency - The lowercase currency code (e.g., "usd")
 * @returns The uppercase currency code (e.g., "USD")
 */
export function currencyToUppercase(currency: string): CurrencyCode {
  return currency.toUpperCase() as CurrencyCode;
}

/**
 * Format a price with proper locale-specific formatting
 * This is especially important for currencies like IDR that need thousands separators
 *
 * @param amount - The price amount
 * @param currency - The currency code
 * @returns Formatted price with proper locale formatting
 */
export function formatPriceWithLocale(
  amount: number,
  currency: string,
): string {
  const currencyCode = currency.toUpperCase() as CurrencyCode;

  // Use proper locale for the currency
  const locale = currencyCode === "IDR" ? "id-ID" : "en-US";

  // No decimal places for currencies that don't display a minor unit: IDR
  // (Stripe 2-decimal but conventionally shown whole) and Stripe zero-decimal
  // currencies like JPY / KRW.
  const noDecimals =
    currencyCode === "IDR" || isStripeZeroDecimal(currencyCode);
  const options: Intl.NumberFormatOptions = {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: noDecimals ? 0 : 2,
    maximumFractionDigits: noDecimals ? 0 : 2,
  };

  return new Intl.NumberFormat(locale, options).format(amount);
}
